import React from 'react';
import { ProductAttributeField } from '../types';
import AttributeField from './AttributeField';

export interface ProductAttributesEditorProps {
  attributes: ProductAttributeField[];
  values: Record<number, string>; // attributeDefinitionId → raw value
  errors?: Record<number, string>;
  onValueChange: (attributeDefinitionId: number, value: string) => void;
  /** Shown when the list is empty (e.g. no custom attributes defined yet). */
  emptyText?: string;
  /** Render fields in N columns on wide screens (defaults to 1). */
  columns?: 1 | 2;
}

/**
 * Renders a list of attribute input fields. Which attributes appear is decided
 * by the caller (module block always; custom attributes filtered by scope and
 * the product's category) — there is no attribute-set selection step.
 */
const ProductAttributesEditor: React.FC<ProductAttributesEditorProps> = ({
  attributes, values, errors, onValueChange, emptyText, columns = 1,
}) => {
  if (attributes.length === 0) {
    return emptyText ? <p className="ims-form-hint" style={{ margin: 0 }}>{emptyText}</p> : null;
  }

  const sorted = attributes
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  const gridStyle: React.CSSProperties = columns === 2
    ? { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 16px' }
    : {};

  return (
    <div style={gridStyle}>
      {sorted.map((attr) => (
        <AttributeField
          key={attr.attributeDefinitionId}
          name={attr.name}
          dataType={attr.dataType}
          options={attr.options}
          unit={attr.unit}
          isRequired={attr.isRequired}
          value={values[attr.attributeDefinitionId] ?? ''}
          error={errors?.[attr.attributeDefinitionId]}
          onChange={(v) => onValueChange(attr.attributeDefinitionId, v)}
        />
      ))}
    </div>
  );
};

export default ProductAttributesEditor;
